import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { ApiError, client, csrfHeader, unwrap } from "@/api/client";
import type { components } from "@/api/schema";
import { t } from "@/i18n";

export type Mailbox = components["schemas"]["Mailbox"];
export type MailMessage = components["schemas"]["MailMessageList"];
export type MailMessageDetail = components["schemas"]["MailMessageDetail"];
export type MailAttachment = components["schemas"]["MailAttachment"];
export type Recipient = components["schemas"]["Recipient"];
export type RecipientSuggestion = components["schemas"]["RecipientSuggestion"];
export type MailFolder = components["schemas"]["MailFolderEnum"];
export type ComposeInput = components["schemas"]["ComposeRequest"];
export type DraftInput = components["schemas"]["DraftRequest"];

/**
 * Everything mail hangs off one key, so a single socket event invalidates the
 * lot: the server's `mail.message` says *that* the mailbox changed and never
 * which message (.docs/09 §7), so narrowing here would only guess.
 */
export const MAIL_KEY = ["mail"] as const;

/** `/correo?carpeta=recibidos`: Spanish in the URL, English in the API (D18). */
const FOLDERS = { recibidos: "inbox", enviados: "sent", borradores: "draft" } as const;

export type FolderParam = keyof typeof FOLDERS;

export const FOLDER_PARAMS = Object.keys(FOLDERS) as FolderParam[];

/** Straight off the URL bar, so `?carpeta=banana` lands on Recibidos. */
export function folderFromParam(raw: string | null): FolderParam {
  return FOLDER_PARAMS.includes(raw as FolderParam)
    ? (raw as FolderParam)
    : "recibidos";
}

export function apiFolder(folder: FolderParam): MailFolder {
  return FOLDERS[folder];
}

/**
 * The connected mailbox, or `null`.
 *
 * A 404 here is an answer, not a failure: "no has conectado tu correo" is the
 * card's first state, and treating it as an error would put a red box in front
 * of every seller who has not connected yet.
 */
export function useMailbox() {
  return useQuery({
    queryKey: [...MAIL_KEY, "mailbox"],
    queryFn: async (): Promise<Mailbox | null> => {
      const { data, error, response } = await client.GET("/api/mail/mailbox/");
      if (response.status === 404) return null;
      if (error !== undefined || data === undefined) {
        throw new ApiError(t("errors.mailbox"), response.status);
      }
      return data;
    },
  });
}

/**
 * Ask the server for Zoho's sign-in URL (D31).
 *
 * The seller's password is typed on that page, never here — this mutation
 * sends an address and nothing else.
 */
export function useConnectMailbox() {
  return useMutation({
    mutationFn: (email: string) =>
      unwrap(
        client.POST("/api/mail/mailbox/connect/", { body: { email } }),
        t("errors.connectMail"),
      ),
  });
}

export function useDisconnectMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // 204: no body to unwrap, so the status is the whole answer.
      const { error, response } = await client.DELETE("/api/mail/mailbox/");
      if (error !== undefined || !response.ok) {
        throw new ApiError(t("errors.disconnectMail"), response.status);
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  });
}

/**
 * The name recipients see next to the seller's address (D37).
 *
 * Set in Zoho, not here: the server answers with the name Zoho reports after
 * the change, and refuses when that is not the one typed.
 */
export function useRenameSender() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) =>
      unwrap(
        client.PATCH("/api/mail/mailbox/", { body: { display_name: displayName } }),
        t("errors.renameSender"),
      ),
    onSuccess: (mailbox) => queryClient.setQueryData([...MAIL_KEY, "mailbox"], mailbox),
  });
}

/** Two letters: one matches most of a 15-year contact base (the server agrees). */
export const MIN_RECIPIENT_LETTERS = 2;

/**
 * Who the composer offers as the seller types (D38).
 *
 * Colleagues first, then the addresses this seller has written to before, then
 * the contacts of the accounts they may work on. The server decides all three —
 * the SPA must not be the thing that knows whose contacts these are.
 */
export function useRecipientSuggestions(term: string) {
  const wanted = term.trim();
  return useQuery({
    queryKey: [...MAIL_KEY, "recipients", wanted],
    enabled: wanted.length >= MIN_RECIPIENT_LETTERS,
    queryFn: () =>
      unwrap(
        client.GET("/api/mail/recipients/", { params: { query: { q: wanted } } }),
        t("errors.recipients"),
      ),
    // A keystroke is not worth a second request for the same letters.
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Autosave (D39): the first call creates the draft, the rest update it.
 *
 * 204 means "nothing worth saving yet" — an empty composer leaves no row — and
 * comes back as `null` rather than as an error.
 */
export function useSaveDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      draft,
    }: {
      id: number | null;
      draft: DraftInput;
    }): Promise<MailMessageDetail | null> => {
      const { data, error, response } =
        id === null
          ? await client.POST("/api/mail/drafts/", { body: draft })
          : await client.PATCH("/api/mail/drafts/{id}/", {
              params: { path: { id } },
              body: draft,
            });
      if (response.status === 204) return null;
      if (error !== undefined || data === undefined) {
        throw new ApiError(t("errors.saveDraft"), response.status);
      }
      return data;
    },
    // Borradores is a folder like the others, so the list it shows is the one
    // every other mail query already invalidates.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  });
}

export function useDiscardDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { error, response } = await client.DELETE("/api/mail/drafts/{id}/", {
        params: { path: { id } },
      });
      if (error !== undefined || !response.ok) {
        throw new ApiError(t("errors.discardDraft"), response.status);
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  });
}

/** *Enviar* on a reopened draft: the row becomes a queued email, server-side. */
export function useSendDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(
        client.POST("/api/mail/drafts/{id}/send/", { params: { path: { id } } }),
        t("errors.sendMail"),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  });
}

export type MailFilters = { folder: FolderParam; search: string; page: number };

export function useMailMessages(filters: MailFilters) {
  return useQuery({
    queryKey: [...MAIL_KEY, "messages", filters],
    queryFn: () =>
      unwrap(
        client.GET("/api/mail/messages/", {
          params: {
            query: {
              folder: apiFolder(filters.folder),
              page: filters.page,
              ...(filters.search ? { search: filters.search } : {}),
            },
          },
        }),
        t("errors.mail"),
      ),
    // Without it the list blanks on every page change and every search, which
    // reads as the mailbox emptying.
    placeholderData: keepPreviousData,
  });
}

/**
 * One email, with its body.
 *
 * The first open is what fetches the body from Zoho and caches it server-side
 * (D35), so this is the request that spends from the per-minute budget. Hence
 * the long `staleTime`: re-reading a message already on screen must not cost
 * another call.
 */
export function useMailMessage(id: number | null) {
  return useQuery({
    queryKey: [...MAIL_KEY, "message", id],
    enabled: id !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/mail/messages/{id}/", {
          params: { path: { id: id as number } },
        }),
        t("errors.mailMessage"),
      ),
    staleTime: 5 * 60_000,
  });
}

/** The red count beside *Correo* (.docs/09 §7). */
export function useUnreadMail() {
  return useQuery({
    queryKey: [...MAIL_KEY, "unread"],
    queryFn: () => unwrap(client.GET("/api/mail/unread/"), t("errors.unreadMail")),
    // The one number on screen that must not be stale when someone comes back
    // to a tab that sat in the background all morning.
    refetchOnWindowFocus: true,
  });
}

export function useSendMail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ComposeInput) =>
      unwrap(client.POST("/api/mail/messages/", { body: input }), t("errors.sendMail")),
    // The row comes back `queued` — Zoho is the worker's problem — so the list
    // has a new entry to show at once, in Enviados.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  });
}

export function useSetMailRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }: { id: number; read: boolean }) =>
      unwrap(
        read
          ? client.POST("/api/mail/messages/{id}/read/", { params: { path: { id } } })
          : client.POST("/api/mail/messages/{id}/unread/", {
              params: { path: { id } },
            }),
        t("errors.markRead"),
      ),
    onSuccess: (message) => {
      queryClient.setQueryData(
        [...MAIL_KEY, "message", message.id],
        (previous: MailMessageDetail | undefined) =>
          previous ? { ...previous, is_read: message.is_read } : previous,
      );
      void queryClient.invalidateQueries({ queryKey: MAIL_KEY });
    },
  });
}

export function useRetryMail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(
        client.POST("/api/mail/messages/{id}/retry/", { params: { path: { id } } }),
        t("errors.retryMail"),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  });
}

/**
 * Open the ten-minute window in which Zoho's first webhook request may hand
 * over its signing key, and get back the URL to paste into Zoho (.docs/09 §8.3).
 */
export function useArmWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(client.POST("/api/mail/mailbox/webhook/arm/"), t("errors.armWebhook")),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  });
}

/**
 * Hand a file to Zoho now; the email that carries it is composed after.
 *
 * Not through `client`: a multipart body needs the browser's own boundary,
 * which `openapi-fetch` cannot express — so this is the one call that adds the
 * CSRF header itself.
 */
export function useUploadAttachment() {
  return useMutation({
    mutationFn: async (file: File): Promise<MailAttachment> => {
      const body = new FormData();
      body.append("file", file);
      const response = await fetch("/api/mail/attachments/", {
        method: "POST",
        body,
        credentials: "same-origin",
        headers: csrfHeader(),
      });
      if (!response.ok) {
        const problem = (await response.json().catch(() => null)) as {
          detail?: string;
        } | null;
        throw new ApiError(problem?.detail ?? t("errors.attachment"), response.status);
      }
      return (await response.json()) as MailAttachment;
    },
  });
}

export function recipientsLabel(recipients: readonly Recipient[]): string {
  return recipients.map((one) => one.name || one.address).join(", ");
}

export function senderLabel(message: MailMessage | MailMessageDetail): string {
  return message.from_name || message.from_address;
}

/** `Propuesta` → `Re: Propuesta`, and `Re: Propuesta` → itself. */
export function replySubject(subject: string): string {
  const trimmed = subject.trim();
  return /^re:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}

/** What a seller typed in a recipients box, as addresses. */
export function parseRecipients(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((one) => one.trim().toLowerCase())
    .filter(Boolean);
}
